// =============================================================================
// PEDRA — src/components/OptionsPanel.tsx
// Bloco "Opcoes" da barra lateral: as opcoes de uso diario (espessura de corte,
// etiquetas, uma chapa so, material, fita de borda e veio) + atalho para o
// modal de configuracoes.
//
// Este arquivo tambem exporta os campos reutilizados pelos modais deste lote
// (`DimensionField`, `ToggleField`, `SelectField`, `HelpHint`).
// =============================================================================

import { useEffect, useId, useRef, useState } from 'react';
import type { ChangeEvent, ReactElement, ReactNode } from 'react';

import type { Micron, Settings, Unit } from '../core/types';
import {
  formatLengthTrim,
  fromMicronTrim,
  KERF_DECIMALS,
  KERF_UNIT,
  parseDimension,
  UNIT_SUFFIX,
} from '../core/units';
import { LIMITS } from '../core/validation';

// -----------------------------------------------------------------------------
// PECAS REUTILIZAVEIS DE FORMULARIO
// -----------------------------------------------------------------------------

export interface HelpHintProps {
  text: string;
}

/**
 * Icone (?) com explicacao em pt-BR. E focavel pelo teclado e a bolha e feita
 * so com CSS (`content: attr(data-tip)`), sem dependencia externa.
 */
export function HelpHint({ text }: HelpHintProps): ReactElement {
  return (
    <span
      className="opt-hint"
      role="note"
      tabIndex={0}
      data-tip={text}
      aria-label={`Ajuda: ${text}`}
    >
      <span aria-hidden="true">?</span>
    </span>
  );
}

export interface ToggleFieldProps {
  id: string;
  label: string;
  help: string;
  checked: boolean;
  onChange(value: boolean): void;
  disabled?: boolean;
  /** Botao opcional exibido a direita (ex.: "Gerenciar fitas"). */
  action?: ReactNode;
}

/** Interruptor booleano com rotulo, tooltip e acao opcional. */
export function ToggleField(props: ToggleFieldProps): ReactElement {
  const { id, label, help, checked, onChange, disabled = false, action } = props;
  return (
    <div className="opt-row opt-row--toggle">
      <input
        id={id}
        className="opt-switch"
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        title={help}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
      />
      <label className="opt-row__label" htmlFor={id}>
        {label}
      </label>
      <HelpHint text={help} />
      {action ? <span className="opt-row__action">{action}</span> : null}
    </div>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectFieldProps {
  id: string;
  label: string;
  help: string;
  value: string;
  options: readonly SelectOption[];
  onChange(value: string): void;
  disabled?: boolean;
}

/** Campo de escolha unica com rotulo em cima e tooltip. */
export function SelectField(props: SelectFieldProps): ReactElement {
  const { id, label, help, value, options, onChange, disabled = false } = props;
  return (
    <div className="opt-field">
      <div className="opt-field__head">
        <label className="opt-field__label" htmlFor={id}>
          {label}
        </label>
        <HelpHint text={help} />
      </div>
      <select
        id={id}
        className="opt-select"
        value={value}
        disabled={disabled}
        title={help}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export interface DimensionFieldProps {
  id: string;
  label: string;
  help: string;
  /** Valor atual em Micron. */
  value: Micron;
  unit: Unit;
  decimals: number;
  /** Limites em Micron (inclusivos). */
  min?: Micron;
  max?: Micron;
  onCommit(value: Micron): void;
  /** Aviso amarelo opcional, independente do erro de digitacao. */
  warning?: string | null;
  disabled?: boolean;
}

/**
 * Entrada de medida: aceita "4", "4,5", "1/8\"", "1200 mm"... Tudo o que
 * `parseDimension` entende. Comita em Micron a cada digitacao valida e so
 * reformata o texto quando o valor muda por fora (desfazer, troca de unidade).
 */
export function DimensionField(props: DimensionFieldProps): ReactElement {
  const {
    id,
    label,
    help,
    value,
    unit,
    decimals,
    min = 0,
    max = LIMITS.MAX_DIMENSION,
    onCommit,
    warning = null,
    disabled = false,
  } = props;

  // Casas decimais aqui sao TETO, nao piso: em metro, "0,3" e melhor de ler e
  // de conferir do que "0,300". Quem quiser coluna alinhada usa `fromMicron`.
  const format = (v: Micron): string => fromMicronTrim(v, unit, decimals);

  const [draft, setDraft] = useState<string>(() => format(value));
  const [error, setError] = useState<string | null>(null);

  // Guardam o ultimo estado "conhecido" para distinguir mudanca interna
  // (digitacao) de mudanca externa (desfazer, troca de unidade).
  const lastValueRef = useRef<Micron>(value);
  const lastUnitRef = useRef<Unit>(unit);
  const lastDecimalsRef = useRef<number>(decimals);

  useEffect(() => {
    const changedOutside =
      value !== lastValueRef.current ||
      unit !== lastUnitRef.current ||
      decimals !== lastDecimalsRef.current;
    if (!changedOutside) return;
    lastValueRef.current = value;
    lastUnitRef.current = unit;
    lastDecimalsRef.current = decimals;
    setDraft(format(value));
    setError(null);
  }, [value, unit, decimals]);

  const handleChange = (raw: string): void => {
    setDraft(raw);
    const trimmed = raw.trim();
    if (trimmed === '') {
      setError('Informe uma medida.');
      return;
    }
    const parsed = parseDimension(trimmed, unit);
    if (parsed === null) {
      setError('Medida inválida. Use, por exemplo, 4 ou 4,5.');
      return;
    }
    if (parsed < min) {
      setError(`O menor valor aceito é ${formatLengthTrim(min, unit, decimals)}.`);
      return;
    }
    if (parsed > max) {
      setError(`O maior valor aceito é ${formatLengthTrim(max, unit, decimals)}.`);
      return;
    }
    setError(null);
    lastValueRef.current = parsed;
    onCommit(parsed);
  };

  const handleBlur = (): void => {
    // Sai do campo com texto invalido: volta ao ultimo valor bom.
    setDraft(format(value));
    if (error !== null) setError(null);
  };

  const errorId = `${id}-erro`;

  return (
    <div className="opt-field">
      <div className="opt-field__head">
        <label className="opt-field__label" htmlFor={id}>
          {label}
        </label>
        <HelpHint text={help} />
      </div>
      <div className="opt-input-wrap">
        <input
          id={id}
          className="opt-input"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          spellCheck={false}
          value={draft}
          disabled={disabled}
          title={help}
          aria-invalid={error !== null}
          aria-describedby={error !== null ? errorId : undefined}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e.target.value)}
          onBlur={handleBlur}
        />
        <span className="opt-input__suffix" aria-hidden="true">
          {UNIT_SUFFIX[unit]}
        </span>
      </div>
      {error !== null ? (
        <p className="opt-field__error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
      {error === null && warning ? <p className="opt-field__warning">{warning}</p> : null}
    </div>
  );
}

// -----------------------------------------------------------------------------
// PAINEL DE OPCOES
// -----------------------------------------------------------------------------

export interface OptionsPanelProps {
  settings: Settings;
  onChange(patch: Partial<Settings>): void;
  onOpenSettings(): void;
  onOpenEdgeBands(): void;
}

/** Textos de ajuda, num so lugar para facilitar revisao. */
const HELP = {
  kerf:
    'Quanto de material a lâmina consome em cada corte, SEMPRE em milímetros — ' +
    'mesmo com as medidas em metro ou em polegada, porque é assim que o disco ' +
    'é vendido. Serra de bancada tira de 3 a 4 mm; disco de seccionadora, cerca ' +
    'de 4,4 mm; fio diamantado para pedra, cerca de 1 mm. Com 0 o cálculo ' +
    'ignora a perda de corte.',
  labels:
    'Mostra a coluna "Nome" na tabela de peças e escreve o nome dentro de cada ' +
    'peça no desenho da chapa.',
  singleStock:
    'Usa no máximo UMA chapa de cada linha do estoque. Serve para responder ' +
    '"cabe tudo em uma chapa só?" sem apagar as quantidades cadastradas.',
  material:
    'Separa peças e chapas por material: uma peça de "MDF 15 mm" só é cortada ' +
    'de uma chapa de "MDF 15 mm". Desligado, tudo vira um material único.',
  edgeBanding:
    'Habilita a fita de borda por peça. A espessura da fita é descontada da ' +
    'medida de corte, para que a peça pronta fique com a medida informada.',
  grain:
    'Respeita a direção do veio (ou do movimento da pedra): peças com veio ' +
    'definido não podem ser giradas 90° para caber melhor.',
} as const;

export function OptionsPanel(props: OptionsPanelProps): ReactElement {
  const { settings, onChange, onOpenSettings, onOpenEdgeBands } = props;
  const uid = useId();

  const kerfWarning =
    settings.kerf > LIMITS.WARN_KERF
      ? 'Espessura de corte alta para uma serra comum — confira o valor.'
      : null;

  return (
    <section className="opt-panel" aria-labelledby={`${uid}-titulo`}>
      <h3 className="opt-panel__title" id={`${uid}-titulo`}>
        Opções
      </h3>

      {/*
        O kerf e o UNICO campo de medida que ignora `settings.unit`: ele fala
        milimetro sempre (ver KERF_UNIT em core/units). Num projeto em metro,
        "0,004" seria ilegivel e perigoso de digitar; "4 mm" e o numero que
        esta escrito no disco. O valor guardado continua em Micron, entao
        trocar a unidade do projeto nao mexe nele.
      */}
      <DimensionField
        id={`${uid}-kerf`}
        label="Espessura de corte (serra)"
        help={HELP.kerf}
        value={settings.kerf}
        unit={KERF_UNIT}
        decimals={KERF_DECIMALS}
        min={0}
        max={LIMITS.MAX_KERF}
        warning={kerfWarning}
        onCommit={(kerf) => onChange({ kerf })}
      />

      <div className="opt-toggles">
        <ToggleField
          id={`${uid}-labels`}
          label="Etiquetas nas peças"
          help={HELP.labels}
          checked={settings.labelsOnPanels}
          onChange={(labelsOnPanels) => onChange({ labelsOnPanels })}
        />

        <ToggleField
          id={`${uid}-single`}
          label="Usar apenas uma chapa do estoque"
          help={HELP.singleStock}
          checked={settings.useSingleStockUnit}
          onChange={(useSingleStockUnit) => onChange({ useSingleStockUnit })}
        />

        <ToggleField
          id={`${uid}-material`}
          label="Considerar material"
          help={HELP.material}
          checked={settings.considerMaterial}
          onChange={(considerMaterial) => onChange({ considerMaterial })}
        />

        <ToggleField
          id={`${uid}-edge`}
          label="Fita de borda"
          help={HELP.edgeBanding}
          checked={settings.edgeBandingEnabled}
          onChange={(edgeBandingEnabled) => onChange({ edgeBandingEnabled })}
          action={
            <button
              type="button"
              className="btn btn--ghost btn--sm opt-manage"
              onClick={onOpenEdgeBands}
              disabled={!settings.edgeBandingEnabled}
              title="Cadastrar, renomear e excluir tipos de fita de borda"
            >
              Gerenciar fitas
            </button>
          }
        />

        <ToggleField
          id={`${uid}-grain`}
          label="Considerar direção do veio"
          help={HELP.grain}
          checked={settings.considerGrain}
          onChange={(considerGrain) => onChange({ considerGrain })}
        />
      </div>

      <button
        type="button"
        className="btn btn--ghost opt-more"
        onClick={onOpenSettings}
        title="Unidade, casas decimais, prioridade da otimização e mais"
      >
        <span aria-hidden="true">⚙</span> Mais configurações
      </button>
    </section>
  );
}

export default OptionsPanel;
